import { useEffect, useState } from 'react';
import { isNil } from 'lodash-es';
import { useQueryClient } from 'react-query';

import { MsTeamsTargetingClient, MSTeamsUser, RosterClient } from 'jf/api';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

const listener = (event) => {
  // Cancel the event as stated by the standard.
  event.preventDefault();
  // Chrome requires returnValue to be set.
  event.returnValue = '';
};

export const useStudyMSTeamsTargetUpdater = (studyRef: string | undefined) => {
  const queryClient = useQueryClient();

  const { data: msteamsTargets } = useClientQuery(MsTeamsTargetingClient.getStudyMsteamsTargets, {
    studyRef,
  });

  const { mutateAsync: updateStudyRosterMsteamsTargets } = useClientMutation(
    RosterClient.updateStudyRosterMsteamsTargets
  );
  const { mutateAsync: deleteStudyMsteamsTargets } = useClientMutation(
    MsTeamsTargetingClient.deleteStudyMsteamsTargets
  );

  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // if we're updating, we should prevent user from refreshing the page
    // this would make it so some queries will not get a chance to fire
    // and reload will remove users that looked like they were set.
    if (isUpdating) {
      window.addEventListener('beforeunload', listener);
    } else {
      window.removeEventListener('beforeunload', listener);
    }
  }, [isUpdating]);

  const update = async (newMsteamsTargets: MSTeamsUser[]) => {
    if (isNil(msteamsTargets)) {
      return;
    }

    const queries: Promise<any>[] = [];

    // create new targets
    const msteamsTargetsToCreate = newMsteamsTargets.filter((target) => {
      const targetAlreadyExists = msteamsTargets.find((oldTarget) => oldTarget.id === target.id);
      return !targetAlreadyExists;
    });
    for (const msteamsTarget of msteamsTargetsToCreate) {
      queries.push(
        // This is where we should save each new msteams user to be targeted
        updateStudyRosterMsteamsTargets({
          studyRef: studyRef!,
          msteamsUserRef: msteamsTarget.id,
        })
      );
    }

    // delete removed targets
    const msteamsTargetsToDelete = msteamsTargets.filter((target) => {
      const targetStillExists = newMsteamsTargets.find((newTarget) => target.id === newTarget.id);
      return !targetStillExists;
    });

    if (msteamsTargetsToDelete.length) {
      queries.push(
        deleteStudyMsteamsTargets({
          studyRef: studyRef!,
          msteamsUserRef: msteamsTargetsToDelete.map((target) => target.id),
        })
      );
    }
    if (queries.length) {
      setIsUpdating(true);
      return Promise.allSettled(queries).then(() => {
        setIsUpdating(false);
        queryClient.refetchQueries(['GET_STUDY_MSTEAMS_TARGETS', studyRef]);
      });
    }
  };

  return { update, isUpdating };
};
